import { QueryFunctionContext, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "../../../use-access-token";
import { queryKeys } from "../../queryKeys";
import { configuration } from "../../../../configuration";
import { FetchError, GetAgendaItemTextContentDto } from "../../../types";
import { useSignalR } from "../../../../hooks";
import { useEffect } from "react";
import { SignalRGroup } from "../../../../components";

const useGetAgendaItemTextContentRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getAgendaItemTextContentRequest({
    queryKey,
  }: QueryFunctionContext<
    ReturnType<typeof queryKeys.meetings.getAgendaItemTextContent>
  >): Promise<GetAgendaItemTextContentDto> {
    const accessToken = await getAccessToken();
    const [{ agendaItemGuid }] = queryKey;

    const response = await fetch(`${configuration.apiRootUrl}/agenda-items/${agendaItemGuid}/text-content`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getAgendaItemTextContentRequest };
};

const useGetAgendaItemTextContent = (agendaItemGuid: string) => {
  const { getAgendaItemTextContentRequest } = useGetAgendaItemTextContentRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    signalR.joinGroup(SignalRGroup.MeetingAgendaItemContent(agendaItemGuid));
    return () => signalR.leaveGroup(SignalRGroup.MeetingAgendaItemContent(agendaItemGuid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendaItemGuid]);

  useEffect(() => {
    const handleShouldRefetchAgendaItemContent = (agendaItemGuid: string) => {
      queryClient.invalidateQueries(queryKeys.meetings.getAgendaItemTextContent(agendaItemGuid));
    };

    signalR.onShouldRefetchAgendaItemContent(handleShouldRefetchAgendaItemContent);
    return () => signalR.offShouldRefetchAgendaItemContent(handleShouldRefetchAgendaItemContent);
  }, [queryClient, signalR]);

  return useQuery({
    queryKey: queryKeys.meetings.getAgendaItemTextContent(agendaItemGuid),
    queryFn: getAgendaItemTextContentRequest,
  });
};

export { useGetAgendaItemTextContent };
